import { deleteSchoolUsers } from 'app/crud/schools.crud';
import { createUser, createUserSchools, putLicenseMint, updateUser } from 'app/crud/users.crud';
import { createGroup, deleteGroup } from 'app/crud/usersGroups.crud';
import { USER_ROLES } from 'app/models/user/user.constants';
import { getRoleText } from 'app/models/user/user.messages';
import {
  canSeeFrontOffice,
  isAdminEditorial,
  isHelpDesk,
  isPublisher,
  isStudent,
  isTeacher,
  isUserBackOffice,
} from 'app/models/user/user.role';
import { getDataGeneral, getDataGeneralUpdate } from 'app/models/user/user.serialize';
import { actions as actionsAuth } from 'app/store/ducks/auth.duck';
import store from 'app/store/store';
import { getDomainTenant } from 'app/utils/url';
import axios from 'axios';

// Domain logic now lives in app/models/user/. These re-exports preserve
// the public API for the 30+ callers across the codebase.
export { USER_ROLES };
export { getDataGeneral, getDataGeneralUpdate };
export { isTeacher, isStudent, isAdminEditorial, isHelpDesk, isPublisher, isUserBackOffice, canSeeFrontOffice };
export { getRoleText };

// ---------------------------------------------------------------
// Orchestration / side effects (kept here on purpose)
// ---------------------------------------------------------------

export async function createUserService(data) {
  const newUser = getDataGeneral(data);
  const response = await createUser(newUser);

  if (response.data) {
    const { guid } = response.data.data;
    await fetchUpdateUserGroups(guid, data);
    await fetchUpdateUserSchools(guid, data);
  }

  return response;
}

export async function updateUserService(data) {
  const updateData = getDataGeneralUpdate(data);
  await fetchUpdateUserGroups(data.guid, data);
  await fetchUpdateUserSchools(data.guid, data);

  return await updateUser(updateData);
}

export async function updateUserLicenseMintService({ data, newData }) {
  if (data.has_license_mint !== newData.has_license_mint) {
    const resonse = await putLicenseMint(data.guid, { access: newData.has_license_mint });
    store.dispatch(actionsAuth.updateHasLicenseMint(newData.has_license_mint ? 1 : 0));
    return resonse;
  }
}

export function viewAsUser({ user, schoolGuid, tenant }) {
  let domain = getDomainTenant({ tenant, user });

  if (user.schools?.length) {
    const url = schoolGuid ? user.schools.find((item) => item.guid === schoolGuid).url_access : user.schools[0].url_access;
    domain = url ? `//${url}` : domain;
  }

  return new Promise(async (resolve) => {
    try {
      const response = await axios.post(`users/${user.guid}/login`, { data: {} });

      window.open(`${domain}auth/login/${response?.data?.data?.token}`);
      resolve(response?.data?.data);
    } catch (error) { console.debug(error); }
  });
}

export function viewAsUserOraculo(user) {
  return new Promise(async (resolve) => {
    const response = await axios.post(`users/${user.oraculo_id}/login?provider=oraculo`, {});
    const { frontoffice, token } = response.data.data;

    window.open(`${frontoffice}/login/?user_token=${token}`);
    resolve(response.data.data);
  });
}

// ---------------------------------------------------------------
// Internal: groups / schools update orchestration
// ---------------------------------------------------------------

async function fetchUpdateUserSchools(userGuid, data) {
  if (data.role !== USER_ROLES.teacherAdmin) {
    await fetchUpdateUserSchoolsOnly(userGuid, data);
  } else {
    await fetchUpdateUserSchoolsAdmin(userGuid, data);
  }
}

async function fetchUpdateUserGroups(userGuid, data) {
  const { groups, groupsOld, school } = data;
  const filterCreate = groups.filter((item) => !item?.school_guid || item?.school_guid === school?.value);
  const filterDelete = groupsOld.filter((item) => !groups.find((element) => element.value === item.guid));

  filterCreate.forEach((group) => {
    createGroup(group.school_guid, group.value, { guid: [userGuid] });
  });

  filterDelete.forEach((element) => {
    deleteGroup(element.school_guid, element.guid, { guid: [userGuid] });
  });
}

async function fetchUpdateUserSchoolsOnly(userGuid, data) {
  const { school, schoolsOld } = data;
  const filter = schoolsOld.filter((item) => item.guid !== school?.value).filter((item) => !item.name?.includes(item.guid)); // Quitamos 'personal school of'

  if (school?.value) {
    await createUserSchools(userGuid, [school.value]);
  }

  for (const element of filter) {
    await deleteSchoolUsers(element.guid, { guid: [data.guid] });
  }
}

async function fetchUpdateUserSchoolsAdmin(userGuid, data) {
  const { schools, schoolsOld } = data;

  if (schools.length) {
    await createUserSchools(
      userGuid,
      schools.map((item) => item.value)
    );
  }

  const filterOld = schoolsOld.filter((item) => !schools.find((element) => element.value === item.value));
  for (const element of filterOld) {
    await deleteSchoolUsers(element.guid, { guid: [data.guid] });
  }
}
